import { normalizeEvidenceSourceType } from './evidenceResourceProjection.js';


const SENSITIVE_KEY_FRAGMENTS = [
  'apikey', 'authorization', 'cookie', 'credential', 'header', 'password',
  'providerpayload', 'sdkpayload', 'secret', 'token',
];
const ROLE_VIEW = {
  candidate: { evidenceKind: 'sourcing' },
  reference: { evidenceKind: 'retrieval' },
};

const isRecord = (value) => Boolean(value && typeof value === 'object' && !Array.isArray(value));
const text = (value) => String(value ?? '').trim();
const firstText = (...values) => values.map(text).find(Boolean) || '';
const optionalText = (value) => text(value) || undefined;
const textList = (value) => (Array.isArray(value) ? value.map(text).filter(Boolean) : []);

const safeKey = (value) => {
  const normalized = text(value);
  const compact = normalized.toLowerCase().replace(/[^a-z0-9]/g, '');
  return normalized.length > 0
    && normalized.length <= 64
    && /^[\p{L}\p{N}._:-]+$/u.test(normalized)
    && !SENSITIVE_KEY_FRAGMENTS.some((fragment) => compact.includes(fragment));
};

const projectScalar = (value) => {
  if (value === null || typeof value === 'boolean') return value;
  if (typeof value === 'string') return value.slice(0, 512);
  if (typeof value === 'number' && Number.isFinite(value) && Math.abs(value) <= Number.MAX_SAFE_INTEGER) return value;
  return undefined;
};

const projectAttributes = (value) => (isRecord(value)
  ? Object.fromEntries(Object.entries(value).slice(0, 32)
    .filter(([key]) => safeKey(key))
    .map(([key, item]) => [
      key,
      Array.isArray(item)
        ? item.slice(0, 16).map(projectScalar).filter((entry) => entry !== undefined)
        : projectScalar(item),
    ])
    .filter(([, item]) => item !== undefined))
  : {});

const projectSourceRef = (value) => {
  if (!isRecord(value)) return {};
  return {
    id: text(value.id),
    source_type: text(value.source_type),
    title: text(value.title),
    domain: text(value.domain),
    url: text(value.url),
    published_at: text(value.published_at),
  };
};

const canonicalEvidenceRefs = (value) => (Array.isArray(value)
  ? value.filter(isRecord).map((item) => ({
    id: text(item.id),
    source_type: text(item.source_type),
    title: text(item.title),
    snippet: text(item.snippet),
    url: text(item.url),
    collected_at: text(item.collected_at),
    strength: item.strength === null ? null : projectScalar(item.strength),
  }))
  : []);

const legacyEvidenceRefs = (value) => (Array.isArray(value)
  ? value.filter(isRecord).map((item) => ({
    id: firstText(item.id, item.evidence_id),
    source_type: firstText(item.source_type, item.source),
    title: firstText(item.title, item.source_title),
    snippet: firstText(item.snippet, item.summary, item.content),
    url: firstText(item.url, item.canonical_url, item.source_url),
    collected_at: firstText(item.collected_at, item.timestamp),
    strength: projectScalar(item.strength ?? item.confidence),
  }))
  : []);

const canonicalRiskFlags = (value) => (Array.isArray(value)
  ? value.filter(isRecord).map((item) => ({
    code: text(item.code),
    level: text(item.level),
    summary: text(item.summary),
    source_url: text(item.source_url),
  }))
  : []);

const legacyRiskFlags = (value) => (Array.isArray(value)
  ? value.filter(isRecord).map((item) => ({
    code: firstText(item.code, item.type),
    level: text(item.level),
    summary: firstText(item.summary, item.reason),
    source_url: firstText(item.source_url, item.url),
  }))
  : []);

const projectSourceSummary = (value) => {
  if (!isRecord(value)) return {};
  const primaryReason = text(value.primary_reason);
  const evidenceCount = Number(value.evidence_count);
  return {
    ...(primaryReason ? { primary_reason: primaryReason } : {}),
    ...(value.evidence_count !== null && Number.isFinite(evidenceCount) ? { evidence_count: evidenceCount } : {}),
  };
};

const projectObservations = (value) => (Array.isArray(value)
  ? value.filter(isRecord).map((item) => ({
    stage_id: text(item.stage_id),
    status: text(item.status),
    hit_count: Number.isFinite(Number(item.hit_count)) ? Number(item.hit_count) : 0,
    summary: text(item.summary),
    reason: text(item.reason),
    terminal: item.terminal === true,
  }))
  : []);

const projectSourceMeta = (value) => {
  if (!isRecord(value)) return {};
  const observations = projectObservations(value.observations);
  const entries = ['retrieval_batch', 'query_profile', 'selected_source', 'final_status', 'terminal_stage_id', 'source']
    .map((key) => [key, text(value[key])]).filter(([, item]) => item);
  return {
    ...Object.fromEntries(entries),
    ...(observations.length > 0 ? { observations } : {}),
  };
};

const canonicalRole = (candidate) => {
  const role = text(candidate.result_role).toLowerCase();
  return Object.hasOwn(ROLE_VIEW, role) ? role : '';
};
const hasLegacyIdentity = (candidate) => Boolean(firstText(
  candidate.supplier_id, candidate.supplier_name, candidate.vendor_name,
));
const legacyRole = (candidate) => {
  const role = firstText(candidate.result_role, candidate.sourcing_result_role).toLowerCase();
  if (Object.hasOwn(ROLE_VIEW, role)) return role;
  return hasLegacyIdentity(candidate) ? 'candidate' : 'reference';
};
const hasLegacyProjectionIdentity = (candidate) => {
  if (hasLegacyIdentity(candidate)) return true;
  const identity = firstText(candidate.result_id, candidate.id, candidate.supplier_id);
  const presentation = firstText(
    candidate.result_role, candidate.sourcing_result_role, candidate.source_title,
    candidate.title, candidate.summary, candidate.canonical_url, candidate.source_url, candidate.source_type,
  );
  return Boolean(identity && presentation);
};
const isCanonical = (candidate) => Boolean(
  text(candidate.id) && canonicalRole(candidate) && text(candidate.title) && text(candidate.source_ref?.id),
);

const projectScore = (value) => {
  if (value === null || value === undefined || value === '') return undefined;
  return Number.isFinite(Number(value)) ? Number(value) : undefined;
};
const supplierName = (role, ...values) => (role === 'candidate' ? firstText(...values) : '');
const resolvedSourceMeta = (candidateMeta, eventMeta) => {
  const projected = projectSourceMeta(candidateMeta);
  return Object.keys(projected).length > 0 ? projected : projectSourceMeta(eventMeta);
};

const buildRow = (resolved, runId, query) => ({
  ...resolved,
  run_id: text(runId),
  query: text(query),
});

const projectCanonical = (candidate, runId, query, eventSourceMeta) => {
  const role = canonicalRole(candidate);
  const sourceRef = projectSourceRef(candidate.source_ref);
  const evidenceRefs = canonicalEvidenceRefs(candidate.evidence_refs);
  const canonicalUrl = firstText(sourceRef.url, evidenceRefs[0]?.url);
  const title = text(candidate.title);
  const reasonCodes = textList(candidate.reason_codes);
  return buildRow({
    key: text(candidate.id), result_id: text(candidate.id), evidence_kind: ROLE_VIEW[role].evidenceKind,
    result_role: role, candidate_kind: '', title, supplier_name: supplierName(role, title),
    source_title: firstText(sourceRef.title, title), source_domain: text(sourceRef.domain),
    source_meta: projectSourceMeta(eventSourceMeta), canonical_url: optionalText(canonicalUrl),
    source_type: text(sourceRef.source_type), list_group: '', score: projectScore(candidate.score),
    confidence: '', confidence_detail: null, snippet: text(candidate.summary), matchedTerms: reasonCodes,
    source_url: optionalText(canonicalUrl), match_reasons: [], reason_codes: reasonCodes,
    risk_flags: canonicalRiskFlags(candidate.risk_flags), source_summary: {}, source_ref: sourceRef,
    evidence_refs: evidenceRefs, attributes: projectAttributes(candidate.attributes), unresolved_checks: [],
  }, runId, query);
};

const projectLegacy = (candidate, index, runId, query, eventSourceMeta) => {
  const role = legacyRole(candidate);
  const key = firstText(candidate.id, candidate.supplier_id, `${text(runId)}-candidate-${index}`);
  const evidenceRefs = legacyEvidenceRefs(candidate.evidence_refs);
  const canonicalUrl = firstText(candidate.canonical_url, evidenceRefs[0]?.url, candidate.source_url);
  const matchReasons = textList(candidate.match_reasons);
  const confidenceDetail = projectAttributes(candidate.confidence_detail);
  return buildRow({
    key, result_id: firstText(candidate.result_id, candidate.id, candidate.supplier_id, key),
    evidence_kind: ROLE_VIEW[role].evidenceKind, result_role: role, candidate_kind: text(candidate.candidate_kind),
    title: firstText(candidate.source_title, candidate.title, candidate.supplier_name, candidate.vendor_name, key),
    supplier_name: supplierName(role, candidate.supplier_name, candidate.source_title, candidate.title, candidate.vendor_name, key),
    source_title: firstText(candidate.source_title, candidate.title, candidate.supplier_name),
    source_domain: text(candidate.source_domain), source_meta: resolvedSourceMeta(candidate.source_meta, eventSourceMeta),
    canonical_url: optionalText(canonicalUrl), source_type: normalizeEvidenceSourceType(candidate.source_type || candidate.source),
    list_group: firstText(candidate.list_group, 'pending_verify'), score: projectScore(candidate.match_score ?? candidate.score),
    confidence: text(candidate.confidence), confidence_detail: Object.keys(confidenceDetail).length > 0 ? confidenceDetail : null,
    snippet: firstText(candidate.summary, matchReasons[0]), matchedTerms: matchReasons,
    source_url: optionalText(canonicalUrl), match_reasons: matchReasons, reason_codes: textList(candidate.reason_codes),
    risk_flags: legacyRiskFlags(candidate.risk_flags), source_summary: projectSourceSummary(candidate.source_summary),
    source_ref: projectSourceRef(candidate.source_ref), evidence_refs: evidenceRefs,
    attributes: projectAttributes(candidate.attributes), unresolved_checks: textList(candidate.unresolved_checks),
  }, runId, query);
};

export function projectSourcingCandidate(options) {
  const { candidate, index, query, runId, eventSourceMeta } = { index: 0, query: '', runId: '', eventSourceMeta: {}, ...Object.fromEntries(Object.entries(Object(options)).filter(([, value]) => value !== undefined)) };
  if (!isRecord(candidate)) return null;
  if (isCanonical(candidate)) return projectCanonical(candidate, runId, query, eventSourceMeta);
  if (!hasLegacyProjectionIdentity(candidate)) return null;
  return projectLegacy(candidate, index, runId, query, eventSourceMeta);
}

export default projectSourcingCandidate;
